import { useEffect } from "react";
import { motion } from "framer-motion";
import { useLocation, Link } from "react-router-dom";
import EditorialMarquee from "../components/site/EditorialMarquee";

const sections = [
  {
    slug: "weddings",
    chapter: "I",
    title: "Weddings & Events",
    body: "The quietest hour of a wedding is the loudest chapter of the film. We arrive with three bodies, four lights and one intention — to honour the day without directing it. Ceremonies are documented as they unfold; portraits are engineered as they should be remembered.",
    images: [
      "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1600&q=85",
      "https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=1600&q=85",
      "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?auto=format&fit=crop&w=1600&q=85",
    ],
  },
  {
    slug: "corporate",
    chapter: "II",
    title: "Corporate & Real Estate",
    body: "For founders, funds and firms. Executive headshots that read as portraits, not id cards. Real estate captured to sell the room, not just describe it. Construction inspection sequences delivered as evidence-grade documentation for developers, insurers and site engineers.",
    images: [
      "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=85",
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=85",
      "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1600&q=85",
    ],
  },
  {
    slug: "editorial",
    chapter: "III",
    title: "Creative Editorial",
    body: "Fashion, brand, personal narrative. Editorial work built around a spine — a concept, a movement, a piece of writing. We work with stylists, directors and personal brands to produce imagery that carries weight beyond the season.",
    images: [
      "https://images.pexels.com/photos/9514677/pexels-photo-9514677.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1600",
      "https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?auto=format&fit=crop&w=1600&q=85",
      "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=1600&q=85",
    ],
  },
  {
    slug: "aerials",
    chapter: "IV",
    title: "Aerial Perspectives",
    body: "Two aircraft. One licensed operator. Regulation-friendly, insured, spotter-supported. The Mini 4 Pro for wide narrative; the Avata 2 for interior fly-throughs and dynamic reveals. Every flight is planned around the site, the story and the sky.",
    images: [
      "/aerials/aerial-mini4pro-valley.webp",
      "/aerials/aerial-mini4pro-coast.webp",
      "/aerials/aerial-avata2-shore.webp",
    ],
  },
];

export default function Portfolio() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    }
  }, [hash]);

  return (
    <main className="pt-24 md:pt-28" data-testid="page-portfolio">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="h-eyebrow mb-6">§ Portfolio Archive</div>
        <h1 className="h-display text-5xl md:text-7xl leading-[0.95] max-w-5xl">
          Four disciplines,<br/>
          <span className="italic font-serif-editorial text-[var(--gold)] font-light">held to a single standard.</span>
        </h1>
      </section>

      <div className="my-16 md:my-20"><EditorialMarquee items={["Weddings", "Corporate", "Editorial", "Aerials", "By Appointment"]} /></div>

      {sections.map((s, i) => (
        <section
          key={s.slug}
          id={s.slug}
          className="max-w-[1600px] mx-auto px-6 md:px-12 py-16 md:py-24 border-t border-white/5 scroll-mt-24"
          data-testid={`portfolio-section-${s.slug}`}
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 mb-16">
            <div className="md:col-span-3">
              <div className="numeral text-[8rem] md:text-[12rem] leading-none">{s.chapter}</div>
            </div>
            <div className="md:col-span-9">
              <div className="h-eyebrow mb-4">Chapter {s.chapter}</div>
              <h2 className="h-display text-4xl md:text-6xl leading-[0.95]">{s.title}</h2>
              <p className="font-serif-editorial text-lg md:text-xl leading-relaxed text-[var(--ink)]/80 mt-8 max-w-3xl">{s.body}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.9 }} className="md:col-span-8">
              <div className="clip-frame h-[60vh] md:h-[80vh] bg-[#111]">
                <img src={s.images[0]} alt={`${s.title} — plate 1`} loading="lazy" />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.9, delay: 0.1 }} className="md:col-span-4 flex flex-col gap-6">
              <div className="clip-frame h-[29vh] md:h-[39vh] bg-[#111]">
                <img src={s.images[1]} alt={`${s.title} — plate 2`} loading="lazy" />
              </div>
              <div className="clip-frame h-[29vh] md:h-[39vh] bg-[#111]">
                <img src={s.images[2]} alt={`${s.title} — plate 3`} loading="lazy" />
              </div>
            </motion.div>
          </div>
        </section>
      ))}

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28 border-t border-white/5 text-center">
        <div className="h-eyebrow mb-6">§ Next</div>
        <h2 className="h-display text-4xl md:text-6xl leading-[0.95]">
          Bring us your <span className="italic font-serif-editorial text-[var(--gold)] font-light">next frame.</span>
        </h2>
        <Link
          to="/contact"
          data-testid="portfolio-cta-consultation"
          className="inline-flex items-center gap-3 mt-10 bg-[var(--royal)] hover:bg-[var(--royal-soft)] text-white px-10 py-5 font-sans-display uppercase tracking-[0.3em] text-[11px] transition-colors"
        >
          Discuss Your Vision →
        </Link>
      </section>
    </main>
  );
}
